import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { Project, ProjectVisibility } from '../types';

export default function CreateProject() {
  const { role } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // Developers are always private — no choice
  const isDeveloper = role === 'developer';

  const [name,        setName]        = useState('');
  const [description, setDescription] = useState('');
  const [link,        setLink]        = useState('');
  const [visibility,  setVisibility]  = useState<ProjectVisibility>(isDeveloper ? 'private' : 'public');
  const [submitting,  setSubmitting]  = useState(false);

  const safeUrl = (raw: string) => {
    const t = raw.trim();
    if (!t) return null;
    if (/^https?:\/\//i.test(t)) return t;
    return `https://${t}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { showToast('Project name is required.', 'error'); return; }

    setSubmitting(true);
    try {
      const project = await api.post<Project>('/api/projects', {
        name:        name.trim(),
        description: description.trim() || null,
        link:        safeUrl(link),
        visibility:  isDeveloper ? 'private' : visibility,
      });
      showToast('Project created!', 'success');
      navigate(`/projects/${project.id}`);
    } catch (err: any) {
      showToast(err.message || 'Failed to create project.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
            <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
          </svg>
          Projects
        </button>
      </div>

      <div className="form-card" style={{ maxWidth: 600 }}>
        <h1 className="page-title" style={{ marginBottom: 4 }}>New Project</h1>
        <p className="page-subtitle" style={{ marginBottom: 28 }}>
          Create a project to group related issues together.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="name">Project Name <span style={{ color: 'var(--color-error)' }}>*</span></label>
            <input
              id="name" className="form-input" type="text"
              placeholder="e.g. Website Redesign, Mobile App v2…"
              value={name} onChange={e => setName(e.target.value)}
              autoFocus required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="description">Description <span className="form-label-hint">(optional)</span></label>
            <textarea
              id="description" className="form-textarea" rows={4}
              placeholder="What is this project about?"
              value={description} onChange={e => setDescription(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="link">Project Link <span className="form-label-hint">(optional)</span></label>
            <div className="input-with-icon">
              <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
              </svg>
              <input
                id="link"
                type="url"
                className="form-input input-with-icon-field"
                placeholder="https://github.com/org/repo"
                value={link}
                onChange={e => setLink(e.target.value)}
              />
            </div>
            <span className="form-hint">Repo, Figma file, doc, or any related resource</span>
          </div>

          {/* Visibility — hidden for developers (always private) */}
          <div className="form-group">
            <label className="form-label">Visibility</label>
            {isDeveloper ? (
              <div className="visibility-locked">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
                Private — only you can see this project
              </div>
            ) : (
              <div className="visibility-toggle">
                <button
                  type="button"
                  className={`visibility-option${visibility === 'public' ? ' active' : ''}`}
                  onClick={() => setVisibility('public')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="2" y1="12" x2="22" y2="12"/>
                    <path d="M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/>
                  </svg>
                  Public
                  <span className="visibility-hint">Visible to all company members</span>
                </button>
                <button
                  type="button"
                  className={`visibility-option${visibility === 'private' ? ' active' : ''}`}
                  onClick={() => setVisibility('private')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0110 0v4"/>
                  </svg>
                  Private
                  <span className="visibility-hint">Only you can see this project</span>
                </button>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/')}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? <><span className="btn-spinner" /> Creating…</> : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
